import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Recaptcha from "react-google-recaptcha";
import { library, dom } from "@fortawesome/fontawesome-svg-core";
import { faCircleXmark } from "@fortawesome/pro-regular-svg-icons/faCircleXmark";
import { faInboxFull } from "@fortawesome/pro-regular-svg-icons/faInboxFull";
import { faClose } from "@fortawesome/pro-regular-svg-icons/faClose";
import { faCaretUp } from "@fortawesome/pro-solid-svg-icons/faCaretUp";

import ChecklistHeader from "./Header";
import WhiteCard from "./WhiteCard";
import ChecklistTitle from "./Title";
import ChecklistInputField from "./InputField";
import ChecklistSelect from "./Select";
import ChecklistBrandDemographic from "./BrandDemographic";
import ChecklistBrandGols from "./BrandGols";
import ChecklistDisclaimer from "./Disclaimer";
import ChecklistSidebar from "./Sidebar";
import ChecklistModal from "./ChecklistModal";
import Collapsible from "./Collapsible";
import ChecklistErrorModal from "./ChecklistErrorModal";
import HubspotCountry from "../../../libs/data/countries.json";

library.add([faCircleXmark, faInboxFull, faCaretUp, faClose]);
dom.watch();

const Checklist = ({ viewModel }) => {
  const { brandDemographics, brandGols, salesRepId } = viewModel;

  // useState for the submission modal
  const [openModal, setOpenModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);

  let siteKey = "";
  if (typeof localStorage !== "undefined") {
    siteKey = localStorage.getItem("recaptchaSiteKey");
  }
  const recaptcha = useRef();

  // format for the initial form data
  const initialData = {
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    country: "",
    promotionDate: "",
    notes: "",
    keywords: "",
    salesRepId, // Used to define who the submission goes to.
    demos: brandDemographics.map((brandDemographic) => ({
      ...brandDemographic,
      brandDemos: brandDemographic?.brandDemos?.map((demo) => ({
        ...demo,
        checked: false
      })),
      demoGroups: brandDemographic?.demoGroups?.map((group) => ({
        ...group,
        groupValues: group?.groupValues?.map((value) => ({ ...value, checked: false }))
      }))
    })),
    gols: brandGols.brandGols.map((brandGol) => ({
      ...brandGol,
      golBindings: brandGol?.golBindings?.map((binding) => ({ ...binding, checked: false }))
    }))
  };

  const [data, setData] = useState(initialData);

  useEffect(() => {}, [data]);

  // This function is designed to assign data for the "static" information section.
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  // This function is designed to select a number of Country checkboxes when a Region group is selected.
  const handleSelectDemographicGroup = (demoId, demoGroupId, demographicGroupValue) => {
    // get a list of the selected demographicValueIds
    const selectedDemosIds = data.demos
      .map((demo) => demo.brandDemos.filter((brandDemo) => brandDemo.checked === true))
      .flat([1])
      .map((demo) => demo.demographicValueId);
    // set checked to true if current selected demos does not include every element of the demo group, otherwise set false
    const checked = !demographicGroupValue.demoGroupValueBindings.every((id) => selectedDemosIds.includes(id));
    const newState = data.demos.map((demo) => {
      if (demo.demographicId === demoId) {
        return {
          ...demo,
          brandDemos: demo.brandDemos.map((brandDemo) => {
            if (demographicGroupValue.demoGroupValueBindings.includes(brandDemo.demographicValueId)) {
              return {
                ...brandDemo,
                checked
              };
            }
            return brandDemo;
          }),
          demoGroups: demo.demoGroups.map((group) => {
            if (group.demographicGroupId === demoGroupId) {
              return {
                ...group,
                groupValues: group.groupValues.map((groupVal) => {
                  if (groupVal.demographicGroupValueId === demographicGroupValue.demographicGroupValueId) {
                    return {
                      ...groupVal,
                      checked
                    };
                  }
                  return groupVal;
                })
              };
            }
            return group;
          })
        };
      }
      return demo;
    });
    setData({ ...data, demos: newState });
  };

  // This function is designed to assign a new "demo" object to state data when a demographic option checkbox is selected.
  const handleSelectDemographicOption = (demoId, demoValueId, checked) => {
    const newState = data.demos.map((demo) => {
      if (demo.demographicId === demoId) {
        return {
          ...demo,
          brandDemos: demo.brandDemos.map((brandDemo) => {
            if (brandDemo.demographicValueId === demoValueId) {
              return {
                ...brandDemo,
                checked
              };
            }
            return brandDemo;
          })
        };
      }
      return demo;
    });
    setData({ ...data, demos: newState });
  };

  // This function is designed to assign a new "gol" object to state data when a GOL checkbox is selected.
  const handleSelectByAudienceTargeting = (golId, checked, totalSelected) => {
    const newState = data.gols.map((gol) => ({
      ...gol,
      golBindings: gol.golBindings.map((binding) => {
        if (binding.golId === golId) {
          /* For awareness, there is a corresponding <span> element below that holds an "id" of "audience-targeting-validation". */
          const validationElement = document.getElementById("audience-targeting-validation");
          if (validationElement) {
            if ((checked && totalSelected + 1 > 10) || (!checked && totalSelected - 1 > 10)) {
              validationElement.scrollIntoView({ behaviour: "smooth", block: "center" });
            }
          }
          return {
            ...binding,
            checked
          };
        }
        return binding;
      })
    }));
    setData({ ...data, gols: newState });
  };

  // This function is designed to unselect any Demographic options, including Demo/Region Groups and individual Countries.
  const handleClearDemographicSelection = (demoSection) => {
    setData({
      ...data,
      demos: data.demos.map((brandDemographic) => ({
        ...brandDemographic,
        // only clear selections for the specific demographic, otherwise keep the same state for other sections
        brandDemos:
          brandDemographic.name === demoSection.name
            ? brandDemographic?.brandDemos?.map((demo) => ({
                ...demo,
                checked: false
              }))
            : brandDemographic.brandDemos,
        demoGroups: brandDemographic?.demoGroups?.map((group) => ({
          ...group,
          groupValues: group?.groupValues?.map((value) => ({ ...value, checked: false }))
        }))
      }))
    });
  };

  // This function is designed to unselect any checkboxes, but only those under a particular Gol group name.
  const handleClearGolsSelection = (golGroupName) => {
    setData({
      ...data,
      gols: data.gols.map((gol) => ({
        ...gol,
        golBindings: gol?.golBindings?.map((binding) => {
          if (gol.name === golGroupName) {
            return {
              ...binding,
              checked: false
            };
          }
          return binding;
        })
      }))
    });
  };

  const SelectedBrandDemographics = (demographics) =>
    demographics.map((demo) => ({
      ...demo,
      brandDemos: demo.brandDemos.filter((brandDemo) => brandDemo.checked === true)
    }));

  const SelectedGols = (gols) =>
    gols.map((gol) => gol.golBindings.filter((binding) => binding.checked === true)).flat([1]);

  const validateRecaptcha = async (event) => {
    event.preventDefault();
    const recaptchaToken = await recaptcha.current.executeAsync();
    handleFormPost(event, recaptchaToken);
  };

  const handleFormPost = async (event, recaptchaToken) => {
    event.preventDefault();

    try {
      await axios.post("/api/v2/checklist/create", {
        data,
        recaptchaToken
      });
      setOpenModal(true);
      setData(initialData);
      event.target.reset();
    } catch (error) {
      setOpenErrorModal(true);
    }
  };

  return (
    <div>
      <div>
        <ChecklistHeader />
        <form onSubmit={validateRecaptcha}>
          <main>
            <section>
              <WhiteCard classNames="white-card-information">
                <section>
                  <ChecklistTitle title="Your Information" />
                  <fieldset className="fieldset-your-information">
                    <ChecklistInputField
                      label="First Name"
                      required
                      type="text"
                      name="firstName"
                      onChange={handleInputChange}
                    />
                    <ChecklistInputField
                      label="Last Name"
                      required
                      type="text"
                      name="lastName"
                      onChange={handleInputChange}
                    />
                    <ChecklistInputField
                      label="Email"
                      required
                      type="email"
                      name="email"
                      onChange={handleInputChange}
                    />
                    <ChecklistInputField
                      label="Company"
                      required
                      type="text"
                      name="company"
                      onChange={handleInputChange}
                    />
                    <ChecklistSelect
                      label="Country"
                      required
                      name="country"
                      value={data.country}
                      defaultOption="Choose a country"
                      selectOptions={HubspotCountry.map((country) => country.name)}
                      onChange={handleInputChange}
                    />
                  </fieldset>
                </section>
                <section>
                  <ChecklistTitle title="Promotion Details" />
                  <fieldset className="fieldset-promotion-details">
                    <ChecklistInputField
                      label="Target Promotion Date"
                      required
                      type="date"
                      name="promotionDate"
                      min={new Date().toISOString().split("T")[0]}
                      max={new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split("T")[0]}
                      onChange={handleInputChange}
                    />
                    <ChecklistInputField
                      label="Notes/Keywords"
                      type="textarea"
                      name="notes"
                      onChange={handleInputChange}
                    />
                  </fieldset>
                </section>
              </WhiteCard>
            </section>
            <section>
              {data.demos.map((brandDemographic) => (
                <WhiteCard key={brandDemographic.demographicId}>
                  <Collapsible
                    title={<ChecklistTitle title={`${brandDemographic.name} Targeting`} />}
                    instructions="(Click to expand/collapse this section)"
                  >
                    <ChecklistBrandDemographic
                      brandDemographic={brandDemographic}
                      selectedBrandDemographics={SelectedBrandDemographics(data.demos)}
                      handleClearDemographicSelection={handleClearDemographicSelection}
                      handleSelectDemographicGroup={handleSelectDemographicGroup}
                      handleSelectDemographicOption={handleSelectDemographicOption}
                      totalSelected={
                        // show the selected for specific section only
                        SelectedBrandDemographics(data.demos).filter(
                          (demo) => demo.demographicId === brandDemographic.demographicId
                        )[0].brandDemos.length
                      }
                    />
                  </Collapsible>
                </WhiteCard>
              ))}
            </section>
            <section>
              <WhiteCard>
                {/* For awareness, there is a corresponding <span> element below that holds an "id" of "audience-targeting-validation". */}
                <span id="audience-targeting-validation" />
                <Collapsible
                  title={<ChecklistTitle title="Interest Areas" required />}
                  instructions="(Click to expand/collapse this section)"
                >
                  <ChecklistBrandGols
                    brandGols={data.gols}
                    handleClearGolsSelection={handleClearGolsSelection}
                    handleSelectByAudienceTargeting={handleSelectByAudienceTargeting}
                    totalSelected={SelectedGols(data.gols).length}
                  />
                </Collapsible>
              </WhiteCard>
            </section>
            <button
              className="checklist-submit-btn"
              type="submit"
              disabled={
                SelectedBrandDemographics(data.demos).filter((demo) => demo.brandDemos.length > 0) < 1 ||
                SelectedGols(data.gols).length < 1 ||
                SelectedGols(data.gols).length > 10
              }
            >
              <i className="fa-regular fa-inbox-full" />
              Send Checklist
            </button>
            <ChecklistDisclaimer />
          </main>
          <aside>
            <ChecklistSidebar
              stateData={data}
              selectedBrandDemographics={SelectedBrandDemographics(data.demos)}
              selectedGols={SelectedGols(data.gols)}
              disabledButton={
                SelectedBrandDemographics(data.demos).filter((demo) => demo.brandDemos.length > 0) < 1 ||
                SelectedGols(data.gols).length < 1 ||
                SelectedGols(data.gols).length > 10
              }
            />
          </aside>
        </form>
      </div>
      <ChecklistModal openModal={openModal} setOpenModal={setOpenModal} />
      <ChecklistErrorModal openErrorModal={openErrorModal} setOpenErrorModal={setOpenErrorModal} />
      <Recaptcha ref={recaptcha} sitekey={siteKey} size="invisible" />
    </div>
  );
};

export default Checklist;
